(() => {
  const OFFER_ID = 'sensory-tee-launch';
  const DISCOUNTS = { 1: 0, 2: 15, 4: 20 };
  // Variant IDs + cart keys stay constant; display data (name, price, image)
  // comes from data-bundle-gifts-json so the UI mirrors the live catalog.
  const GIFTS = [
    { id: '51724047384881', giftId: 'workbook' },
    { id: '51943608877361', giftId: 'stickers' },
    { id: '47446837985585', giftId: 'tote' },
    { id: '51943498055985', giftId: 'golden' },
  ];
  // Canvas gift ladder: tier-1 earns workbook + stickers, tier-2 adds the
  // tote, tier-4 adds the golden ticket.
  const giftsForTier = (count) => count >= 4 ? GIFTS.slice() : count >= 2 ? GIFTS.slice(0, 3) : GIFTS.slice(0, 2);

  const parseJson = (value, fallback) => {
    try { return JSON.parse(value); } catch { return fallback; }
  };
  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
  const money = (cents, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currency || 'USD', minimumFractionDigits: 2,
  }).format(cents / 100);
  const centsFor = (variant) => {
    const raw = variant?.price;
    // product.variants JSON already exposes price in cents (e.g. 9800).
    if (typeof raw === 'number' && Number.isFinite(raw)) return Math.round(raw);
    const value = Number.parseFloat(String(raw ?? '').replace(/[^0-9.,-]/g, '').replace(',', '.'));
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  };
  const optionMarkup = (values, selected) => values.map((value) =>
    `<option value="${escapeHtml(value)}"${value === selected ? ' selected' : ''}>${escapeHtml(value)}</option>`).join('');

  const mount = () => document.querySelectorAll('.cn-pdp').forEach((root) => {
    const host = root.querySelector('[data-bundle-host]');
    const form = root.querySelector('.cn-pdp-form');
    if (!host || !form || host.dataset.bundleMounted === 'true') return;
    host.dataset.bundleMounted = 'true';

    const optionsHost = host.querySelector('[data-bundle-options]');
    const giftsHost = host.querySelector('[data-bundle-gifts]');
    const config = host.querySelector('[data-bundle-config]');
    const confirm = host.querySelector('[data-bundle-confirm]');
    const status = host.querySelector('.commerce-status');
    const currency = root.dataset.currency || 'USD';
    const caretIcon = host.dataset.caretIcon || '';
    const family = root.dataset.family === 'long-sleeve' ? 'long-sleeve' : 'tshirt';
    const primaryProductId = String(root.dataset.primaryProductId || '');
    const pairedProductId = String(root.dataset.pairedProductId || '');
    const colorIndex = Number(root.dataset.colorPosition || 0);
    const sizeIndex = Number(root.dataset.sizePosition || 1);
    const products = parseJson(root.querySelector('[data-bundle-products-json]')?.textContent || '{}', {});
    const mediaByProduct = new Map([...root.querySelectorAll('[data-bundle-product-media]')].map((node) =>
      [node.dataset.productId, parseJson(node.textContent, {})]));
    // Gallery groups are the color authority; these products may not have
    // Shopify variant featured images assigned.
    const imageFor = (productId, color, variant) => {
      const media = mediaByProduct.get(String(productId));
      return media?.colors?.[color] || variant?.featured_image?.src || variant?.featured_media?.preview_image?.src || media?.fallback || '';
    };
    const splitList = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
    const primaryColors = splitList(root.dataset.allowedColors);
    const pairedColors = splitList(root.dataset.pairedColors);
    const productFor = (id) => String(id) === primaryProductId ? products.primary : products.paired;
    const modelFor = (id) => {
      const product = productFor(id) || {};
      // Hardcoded launch colors: keep only variants whose color is allowed
      // for that product's family so stale catalog colors never surface.
      const allowed = String(id) === primaryProductId ? primaryColors : pairedColors;
      const variants = (product.variants || []).filter((variant) =>
        !allowed.length || allowed.includes(variant.options?.[colorIndex]));
      return { product, variants, colorIndex, sizeIndex };
    };
    const valuesFor = (model, index) => [...new Set(model.variants.map((variant) => variant.options?.[index]).filter(Boolean))];
    const variantFor = (model, color, size) => model.variants.find((variant) =>
      variant.options?.[model.colorIndex] === color && variant.options?.[model.sizeIndex] === size);
    const firstAvailable = (model) => model.variants.find((variant) => variant.available) || model.variants[0];
    const choiceFor = (model, index, preferred) => {
      const values = valuesFor(model, index);
      return values.includes(preferred) ? preferred : values.find((value) =>
        model.variants.some((variant) => variant.options?.[index] === value && variant.available)) || values[0] || '';
    };
    const primaryModel = modelFor(primaryProductId);
    let selectedColor = root.dataset.selectedColor || choiceFor(primaryModel, colorIndex, '');
    let selectedSize = root.dataset.selectedSize || choiceFor(primaryModel, sizeIndex, '');
    let activeCount = 2;
    let busy = false;

    const labels = () => family === 'long-sleeve'
      ? { primary: 'Long sleeve tee', paired: 'T-shirt' }
      : { primary: 'T-shirt', paired: 'Long sleeve tee' };

    const rowProductId = (count, index) => {
      // The first product follows the PDP, while the backend only cares about
      // the canonical product counts. Four-item offers always contain two of each.
      const primarySlots = count === 4 ? 2 : count === 1 || count === 2 ? 1 : 0;
      return index < primarySlots ? primaryProductId : pairedProductId;
    };

    const rowVariant = (row) => {
      const model = modelFor(row.dataset.productId);
      return variantFor(model, row.querySelector('[data-option="color"]')?.value, row.querySelector('[data-option="size"]')?.value)
        || firstAvailable(model);
    };

    const syncRow = (row) => {
      const model = modelFor(row.dataset.productId);
      const colorSelect = row.querySelector('[data-option="color"]');
      const sizeSelect = row.querySelector('[data-option="size"]');
      const preview = row.querySelector('.compact-tshirt-preview');
      if (!colorSelect || !sizeSelect || !model.variants.length) return;
      [...colorSelect.options].forEach((option) => {
        option.disabled = !valuesFor(model, model.sizeIndex).some((size) => variantFor(model, option.value, size)?.available);
      });
      if (colorSelect.selectedOptions[0]?.disabled) {
        const available = [...colorSelect.options].find((option) => !option.disabled);
        if (available) colorSelect.value = available.value;
      }
      [...sizeSelect.options].forEach((option) => { option.disabled = !variantFor(model, colorSelect.value, option.value)?.available; });
      if (sizeSelect.selectedOptions[0]?.disabled) {
        const available = [...sizeSelect.options].find((option) => !option.disabled);
        if (available) sizeSelect.value = available.value;
      }
      const variant = rowVariant(row);
      const image = imageFor(row.dataset.productId, colorSelect.value, variant);
      if (preview) {
        if (image) preview.src = image;
        else preview.removeAttribute('src');
        preview.hidden = !image;
        preview.alt = `${model.product.title} in ${colorSelect.value}`;
      }
      row.dataset.variantId = String(variant?.id || '');
      row.querySelector('.tshirt-selection').textContent = `${row.dataset.productLabel} · ${colorSelect.value} · ${sizeSelect.value}`;
    };

    const renderPrices = () => {
      const primaryVariant = variantFor(primaryModel, selectedColor, selectedSize) || firstAvailable(primaryModel);
      const pairedModel = modelFor(pairedProductId);
      const pairedVariant = variantFor(pairedModel, choiceFor(pairedModel, colorIndex, selectedColor), choiceFor(pairedModel, sizeIndex, selectedSize)) || firstAvailable(pairedModel);
      const base = centsFor(primaryVariant);
      const pairedBase = centsFor(pairedVariant);
      [1, 2, 4].forEach((count) => {
        const card = host.querySelector(`[data-bundle-count="${count}"]`);
        if (!card) return;
        const subtotal = count === 1 ? base : count === 2 ? base + pairedBase : (base * 2) + (pairedBase * 2);
        const current = Math.round(subtotal * (1 - DISCOUNTS[count] / 100));
        card.querySelector('.prices-current').textContent = money(current, currency);
        card.querySelector('del').textContent = DISCOUNTS[count] ? money(subtotal, currency) : '';
        card.querySelector('.save').textContent = DISCOUNTS[count] ? `Save ${money(subtotal - current, currency)}` : '';
      });
    };

    const giftCatalog = parseJson(root.querySelector('[data-bundle-gifts-json]')?.textContent || '[]', []);
    const giftDisplay = (gift, index) => ({ ...gift, ...(giftCatalog[index] || {}) });
    // Detail copy mirrors the finalized Canvas node; workbook wording is
    // adapted from hoodie to tee. Keyed by giftId so row names stay dynamic.
    const giftDetails = {
      workbook: { line: 'A space to pause and put your thoughts on paper.', description: 'A guided workbook included with your tee, ready to use as part of your personal wind-down routine.' },
      stickers: { line: 'A little Cloud Nine for your everyday essentials.', description: 'A pack of stickers to personalize your notebook, laptop, or other everyday belongings.' },
      tote: { line: 'A roomy everyday tote for your essentials.', description: 'The Cloud Tote Bag, unlocked with a pair, carries your workbook and daily senses kit wherever you go.' },
      golden: { line: 'A little extra luck tucked into your set.', description: 'The Golden Ticket, unlocked with the four pack, reveals a surprise waiting inside your order.' },
    };
    const giftMoney = (cents) => money(cents, currency).replace(/\.00$/, '');
    const plusSvg = '<svg class="disclosure-plus" width="16" height="16" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"/></svg>';
    const minusSvg = '<svg class="disclosure-minus" width="16" height="16" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128Z"/></svg>';
    const arrowSvg = '<svg class="system-icon" width="12" height="12" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M200,64V168a8,8,0,0,1-16,0V83.31L69.66,197.66a8,8,0,0,1-11.32-11.32L172.69,72H88a8,8,0,0,1,0-16H192A8,8,0,0,1,200,64Z"/></svg>';
    const ensureGiftDialog = () => {
      let dialog = root.querySelector('dialog.gift-dialog');
      if (dialog) return dialog;
      dialog = document.createElement('dialog');
      dialog.className = 'gift-dialog';
      dialog.setAttribute('aria-labelledby', 'gift-title');
      dialog.innerHTML = '<button type="button" class="gift-close" aria-label="Close gift details"><svg class="system-icon" width="20" height="20" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg></button><img alt=""><h2 id="gift-title"></h2><p class="gift-one-line"></p><div class="gift-value-row"><del class="gift-value"></del><strong>FREE \u00b7 $0</strong></div><p class="gift-saving"></p><p class="gift-description"></p>';
      root.append(dialog);
      dialog.querySelector('.gift-close').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      return dialog;
    };
    const renderGifts = () => {
      giftsHost.replaceChildren();
      const tierGifts = giftsForTier(activeCount);
      const display = tierGifts.map(giftDisplay);
      const total = display.reduce((sum, gift) => sum + (Number(gift.price) || 0), 0);
      const countLabel = `${display.length} free ${display.length === 1 ? 'gift' : 'gifts'}`;
      const valueLabel = total > 0 ? `${giftMoney(total)} value \u00b7 included` : 'Included with this set';
      const disclosure = document.createElement('details');
      disclosure.className = 'gift-disclosure bundle-gift-disclosure';
      const summary = document.createElement('summary');
      summary.innerHTML = `<span class="gift-stack bundle-gift-stack" aria-hidden="true">${display.map((gift) => gift.image ? `<img src="${escapeHtml(gift.image)}" alt="">` : '').join('')}</span><span class="gift-disclosure-copy">${escapeHtml(countLabel)}<small>${escapeHtml(valueLabel)}</small></span><span class="gift-chevron" aria-hidden="true">${plusSvg}${minusSvg}</span>`;
      const list = document.createElement('div');
      list.className = 'bundle-gift-images bundle-gift-list';
      display.forEach((gift, index) => {
        const name = gift.name || 'Free gift';
        const value = giftMoney(Number(gift.price) || 0);
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'bundle-gift-item gift-preview-trigger';
        item.dataset.giftName = name;
        item.dataset.giftValue = value;
        item.dataset.giftImage = gift.image || '';
        item.dataset.giftId = tierGifts[index].giftId;
        item.setAttribute('aria-label', `View ${name} details`);
        item.innerHTML = `<span class="gift-photo">${gift.image ? `<img src="${escapeHtml(gift.image)}" alt="${escapeHtml(name)}" width="40" height="40" loading="lazy">` : ''}</span><span class="gift-name">${escapeHtml(name)}<small>View details ${arrowSvg}</small></span><span class="gift-worth"><del>${escapeHtml(value)}</del><strong>FREE</strong></span>`;
        list.append(item);
      });
      disclosure.append(summary, list);
      giftsHost.append(disclosure);
      ensureGiftDialog();
    };
    host.addEventListener('click', (event) => {
      const trigger = event.target.closest('.gift-preview-trigger');
      if (!trigger || !host.contains(trigger)) return;
      const detail = giftDetails[trigger.dataset.giftId] || { line: '', description: '' };
      const dialog = ensureGiftDialog();
      dialog.querySelector('img').src = trigger.dataset.giftImage;
      dialog.querySelector('img').alt = trigger.dataset.giftName;
      dialog.querySelector('h2').textContent = trigger.dataset.giftName;
      dialog.querySelector('.gift-one-line').textContent = detail.line;
      dialog.querySelector('.gift-value').textContent = `${trigger.dataset.giftValue} value`;
      dialog.querySelector('.gift-saving').textContent = `You save ${trigger.dataset.giftValue} \u00b7 included with your bundle`;
      dialog.querySelector('.gift-description').textContent = detail.description;
      dialog.showModal();
    });

    const renderBundle = (count) => {
      activeCount = count;
      const title = config.querySelector('[data-config-title]');
      if (title) title.textContent = count === 1 ? 'Select color & size' : 'Select colors & sizes';
      optionsHost.replaceChildren();
      const names = labels();
      for (let index = 0; index < count; index += 1) {
        const productId = rowProductId(count, index);
        const label = productId === primaryProductId ? names.primary : names.paired;
        const model = modelFor(productId);
        const colors = valuesFor(model, colorIndex);
        const sizes = valuesFor(model, sizeIndex);
        const color = choiceFor(model, colorIndex, selectedColor);
        const size = choiceFor(model, sizeIndex, selectedSize);
        const variant = variantFor(model, color, size) || firstAvailable(model);
        const image = imageFor(productId, color, variant);
        const guide = root.querySelector(`[data-bundle-size-template="${productId}"]`)
          ? `<button type="button" class="compact-size-guide-trigger" data-bundle-size-guide="${escapeHtml(productId)}" aria-label="Size guide for ${escapeHtml(model.product.title)}">Size guide</button>` : '';
        const row = document.createElement('details');
        row.className = 'compact-hoodie-option compact-tshirt-option';
        row.dataset.productId = productId;
        row.dataset.productLabel = label;
        const caret = caretIcon ? `<img class="cn-icon" width="12" height="12" aria-hidden="true" src="${escapeHtml(caretIcon)}" alt="">` : '';
        row.innerHTML = `<summary class="hoodie-summary tshirt-summary"><span class="hoodie-thumbnail-frame tshirt-thumbnail-frame"><img class="compact-tshirt-preview" ${image ? `src="${escapeHtml(image)}"` : 'hidden'} alt="" width="32" height="40"></span><span class="hoodie-summary-text tshirt-summary-text"><span class="hoodie-number tshirt-number">0${index + 1}</span><span class="hoodie-selection tshirt-selection">${escapeHtml(label)} · ${escapeHtml(color)} · ${escapeHtml(size)}</span></span><span class="hoodie-edit tshirt-edit" aria-hidden="true"></span></summary><div class="hoodie-fields"><div class="hoodie-inputs"><label class="compact-select-wrap"><span class="field-label">Color</span><select data-option="color" aria-label="Color for ${escapeHtml(label)} ${index + 1}">${optionMarkup(colors, color)}</select>${caret}</label><div class="compact-select-wrap"><div class="bundle-size-heading"><span class="field-label">Size</span>${guide}</div><select data-option="size" aria-label="Size for ${escapeHtml(label)} ${index + 1}">${optionMarkup(sizes, size)}</select>${caret}</div></div></div>`;
        optionsHost.append(row);
        row.querySelectorAll('select').forEach((select) => select.addEventListener('change', () => { syncRow(row); renderPrices(); }));
        row.addEventListener('toggle', () => { if (row.open) optionsHost.querySelectorAll('details').forEach((other) => { if (other !== row) other.open = false; }); });
        syncRow(row);
      }
      host.querySelectorAll('.cn-offer-card').forEach((card) => {
        const active = Number(card.dataset.bundleCount) === count;
        card.classList.toggle('is-selected', active);
        const radio = card.querySelector('.cn-offer-radio');
        if (radio) radio.checked = active;
      });
      renderGifts();
      activeOfferPlacement();
      renderPrices();
    };

    const activeOfferPlacement = () => {
      const activeOffer = host.querySelector(`[data-bundle-count="${activeCount}"]`);
      // Every tier configures nested inside its own offer card so the
      // color/size selection always reads as part of the variant option.
      if (activeOffer) activeOffer.append(config, confirm);
      config.hidden = false;
      confirm.disabled = false;
      confirm.querySelector('[data-bundle-confirm-label]').textContent = 'Add to cart';
    };

    const cartItems = () => {
      const rows = [...optionsHost.querySelectorAll('.compact-tshirt-option')];
      if (rows.length !== activeCount || rows.some((row) => !row.dataset.variantId)) throw new Error('Choose a color and size for every tee in this set.');
      const groupId = `${OFFER_ID}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      // Underscore-prefixed properties stay hidden in cart/checkout/orders
      // while remaining readable to the Discount Function. Never use
      // visible keys here — shoppers saw offer_id/tier_id/group_id before.
      const items = rows.map((row, index) => ({
        id: Number(row.dataset.variantId),
        quantity: 1,
        properties: {
          _offer_id: OFFER_ID,
          _tier_id: `tier-${activeCount}`,
          _group_id: groupId,
          _role: row.dataset.productId === primaryProductId ? 'primary' : 'paired',
          _component: `gid://shopify/Product/${row.dataset.productId}`,
          _slot: String(index + 1),
        },
      }));
      giftsForTier(activeCount).forEach((gift) => items.push({
        id: Number(gift.id), quantity: 1, properties: {
          _offer_id: OFFER_ID, _tier_id: `tier-${activeCount}`, _group_id: groupId,
          _role: 'gift', _gift_id: gift.giftId, _component: `gid://shopify/ProductVariant/${gift.id}`,
        },
      }));
      return items;
    };

    const addBundle = async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      busy = true;
      status.textContent = '';
      confirm.disabled = true;
      confirm.querySelector('[data-bundle-confirm-label]').textContent = 'Adding…';
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: cartItems() }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this set.');
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { source: OFFER_ID } }));
        window.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: OFFER_ID } }));
        confirm.querySelector('[data-bundle-confirm-label]').textContent = 'Added to cart';
        window.setTimeout(() => { confirm.querySelector('[data-bundle-confirm-label]').textContent = 'Add to cart'; }, 1500);
      } catch (error) {
        status.textContent = error.message;
        confirm.disabled = false;
        confirm.querySelector('[data-bundle-confirm-label]').textContent = 'Add to cart';
      } finally { busy = false; confirm.disabled = false; }
    };

    root.addEventListener('tshirt:selection-change', (event) => {
      if (event.detail?.color) selectedColor = event.detail.color;
      if (event.detail?.size) selectedSize = event.detail.size;
      renderBundle(activeCount);
    });
    form.dataset.bundleManaged = 'true';
    form.addEventListener('submit', addBundle, true);
    host.querySelectorAll('[data-bundle-count]').forEach((card) => {
      const activate = () => renderBundle(Number(card.dataset.bundleCount));
      card.addEventListener('click', (event) => { if (!event.target.closest('select, button, input, textarea, a, .bundle-inline-config')) activate(); });
      card.querySelector('.cn-offer-radio')?.addEventListener('change', activate);
      // Nested selectors and guide buttons own their keyboard interaction.
      card.addEventListener('keydown', (event) => { if (event.target === card && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); activate(); } });
    });
    confirm.addEventListener('click', () => form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })));
    renderBundle(activeCount);
  });

  mount();
  document.addEventListener('shopify:section:load', mount);
  if (document.body) new MutationObserver(mount).observe(document.body, { childList: true, subtree: true });
})();
